/**
 * Commission payment dialog (BE-6b) — one dialog for the three directions.
 *
 * PAID (v3 payout dialog): every payable commission of the recipient is ticked, oldest first; whole commissions only.
 * RECOVERED: money the agent returns, up to what they owe back, into a TC account.
 * WRITTEN_OFF: the TC absorbs part of what the agent owes back; the reason is required.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Checkbox, FormControlLabel, CircularProgress,
} from '@mui/material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import ApiService from '../../services/api.service';
import { commissionService } from '../../services/accounting/commission.service';
import { serverMessage, unwrapList } from '../../utils/apiConvert';

const today = () => new Date().toISOString().slice(0, 10);
const TITLE = { PAID: 'Pay', RECOVERED: 'Recover from', WRITTEN_OFF: 'Write off for' };
const inputSx = { '& .MuiInputBase-input': { fontSize: fs.sm }, '& .MuiInputLabel-root': { fontSize: fs.sm } };
const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));

const CommissionPaymentDialog = ({ open, onClose, direction, recipient, recipients, onDone }) => {
  const [agentId, setAgentId] = useState('');
  const [payable, setPayable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ accountId: '', amount: '', paidAt: today(), reference: '' });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const agent = (recipients || []).find((r) => r.id === agentId) || recipient;
  const owed = Number(agent?.receivable || 0);

  useEffect(() => {
    if (!open) { setAgentId(''); setPayable([]); return; }
    setError(null);
    setSelected([]);
    setPayable([]);
    setAgentId(recipient?.id || '');
    setForm({ accountId: '', amount: '', paidAt: today(), reference: '' });
    ApiService.getTcBankAccounts().then(unwrapList)
      .then((r) => {
        const usable = r.items.filter((a) => a.isActive !== false && ['OPERATIONS', 'CASH'].includes(a.accountType));
        setAccounts(usable);
        if (direction === 'PAID' && usable.length) setForm((f) => ({ ...f, accountId: f.accountId || usable[0].id }));
      })
      .catch(() => setAccounts([]));
  }, [open, recipient, direction]);

  useEffect(() => {
    if (!open || direction !== 'PAID' || !agentId) { setPayable([]); return undefined; }
    let live = true;
    setLoading(true);
    commissionService.getCommissions({ recipientId: agentId, status: 'PAYABLE', page: 0, size: 100 })
      .then((page) => {
        if (!live) return;
        const oldestFirst = [...page.content].reverse();
        setPayable(oldestFirst);
        setSelected(oldestFirst.map((c) => c.id));
      })
      .catch((err) => { if (live) setError(serverMessage(err, 'Failed to load payable commissions')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, direction, agentId]);

  const total = payable.filter((c) => selected.includes(c.id)).reduce((t, c) => t + Number(c.payable || 0), 0);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid = agentId && form.paidAt && form.paidAt <= today() && (
    direction === 'PAID' ? selected.length > 0 && form.accountId
      : direction === 'RECOVERED' ? Number(form.amount) > 0 && twoDecimals(form.amount) && Number(form.amount) <= owed && form.accountId
        : Number(form.amount) > 0 && twoDecimals(form.amount) && Number(form.amount) <= owed && form.reference.trim());

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = { recipientId: agentId, direction, paidAt: form.paidAt, reference: form.reference.trim() };
      if (direction === 'PAID') Object.assign(data, { commissionIds: selected, accountId: form.accountId });
      if (direction === 'RECOVERED') Object.assign(data, { amount: form.amount, accountId: form.accountId });
      if (direction === 'WRITTEN_OFF') Object.assign(data, { amount: form.amount });
      const payment = await commissionService.recordPayment(data);
      onDone(payment);
    } catch (err) {
      setError(serverMessage(err, 'The payment failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>{TITLE[direction]} {agent ? agent.name : 'Agent'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {!recipient && (
            <TextField select size="small" label="Agent *" value={agentId} onChange={(e) => { setAgentId(e.target.value); setSelected([]); }}>
              {(recipients || []).filter((r) => (direction === 'PAID' ? Number(r.payable) > 0 : Number(r.receivable) > 0)).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} - {direction === 'PAID' ? `${formatCurrency(r.payable)} payable` : `owes back ${formatCurrency(r.receivable)}`}
                </MenuItem>
              ))}
            </TextField>
          )}

          {direction === 'PAID' && agentId && (
            <Box>
              <Typography sx={{ fontSize: fs.sm, mb: 1 }}>
                Select commissions to pay ({payable.length} payable):
              </Typography>
              {loading ? <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                : payable.length === 0 ? (
                  <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', py: 1 }}>Nothing payable yet — commissions become payable once the customer has paid the invoice in full.</Typography>
                ) : (
                  <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                    {payable.map((c) => (
                      <FormControlLabel key={c.id} sx={{ display: 'flex', mb: 0.25, mr: 0 }}
                        control={<Checkbox size="small" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />}
                        label={(
                          <Typography sx={{ fontSize: fs.xs }}>
                            {c.taskNumber || c.taskName || 'Task'} &mdash; {c.invoiceNumber} &mdash; {formatCurrency(c.payable)}
                            <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 1 }}>{formatDate(c.createdAt)}</Typography>
                          </Typography>
                        )} />
                    ))}
                  </Box>
                )}
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Total: {formatCurrency(total)}</Typography>
              </Box>
            </Box>
          )}

          {direction !== 'PAID' && agent && (
            <Alert severity="info" sx={{ py: 0 }}>
              {agent.name} owes back {formatCurrency(owed)} — commission paid out on invoices that were later voided.
            </Alert>
          )}
          {direction !== 'PAID' && (
            <TextField size="small" type="number" label="Amount (AED) *" inputProps={{ min: 0, step: '0.01' }}
              value={form.amount} onChange={set('amount')}
              helperText={Number(form.amount) > owed ? `At most ${formatCurrency(owed)}` : ' '} error={Number(form.amount) > owed} />
          )}
          {direction !== 'WRITTEN_OFF' && (
            <TextField select size="small" label={direction === 'PAID' ? 'Pay from' : 'Received into *'} value={form.accountId} onChange={set('accountId')} sx={inputSx}>
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{[a.bankName, a.accountName].filter(Boolean).join(' - ')}{a.accountType === 'CASH' ? ' (cash)' : ''}</MenuItem>
              ))}
            </TextField>
          )}
          <Box display="flex" gap={1}>
            <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
              sx={{ ...inputSx, width: 170 }} value={form.paidAt} onChange={set('paidAt')} />
            <TextField size="small" sx={{ ...inputSx, flex: 1 }} label={direction === 'WRITTEN_OFF' ? 'Reason *' : 'Notes'}
              value={form.reference} onChange={set('reference')}
              placeholder={direction === 'WRITTEN_OFF' ? 'e.g. Agent left — not recoverable' : 'e.g. Week 38 payout'} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" color={direction === 'WRITTEN_OFF' ? 'warning' : 'primary'} disabled={busy || !valid} onClick={submit} sx={{ textTransform: 'none' }}>
          {direction === 'PAID' ? 'Pay Now' : direction === 'RECOVERED' ? 'Record Recovery' : 'Write Off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionPaymentDialog;
